import { useStandardRecordDisciplineListQuery } from '../record-discipline/queries';
import { ConfirmDialog } from '~/components/ConfirmDialog/ConfirmDialog';
import { StandardRecord, StandardRecordDiscipline } from '~/apis/ssf';
import { ContextMenu } from '~/components/ContextMenu/ContextMenu';
import { useDeleteStandardRecordMutation } from '../mutations';
import { Table } from '~/components/Table/Table';
import { useDialog } from '~/hooks/useDialog';
import { Fragment } from 'react/jsx-runtime';
import { toast } from 'react-toastify';

interface Props {
  isLoading: boolean;
  records: StandardRecord[];
}

function Row({ record, disciplines }: { record: StandardRecord; disciplines: StandardRecordDiscipline[] }) {
  const deleteMutation = useDeleteStandardRecordMutation();
  const discipline = disciplines.find(d => d.name === record.discipline);
  const dialog = useDialog();

  async function handleDelete() {
    await toast.promise(deleteMutation.mutateAsync(record.id), {
      success: 'Standardrekord har tagits bort',
      pending: 'Tar bort standardrekord...',
    });
  }

  return (
    <Fragment>
      <Table.Row>
        <Table.Col>{record.district?.name ?? 'Svenskt rekord'}</Table.Col>
        <Table.Col>{record.competition_type.description}</Table.Col>
        <Table.Col>{record.weight_class.is_male ? 'Herr' : 'Dam'}</Table.Col>
        <Table.Col>{record.age_category.label}</Table.Col>
        <Table.Col>{record.weight_class.name}</Table.Col>
        <Table.Col>{discipline?.description}</Table.Col>
        <Table.Col>{record.result}</Table.Col>
        <Table.Col align="right">
          <Table.Context
            items={[
              <ContextMenu.Item label="Redigera" key="edit" to={`${record.id}/edit`} />,
              <ContextMenu.Item label="Ta bort" key="delete" onClick={dialog.open} danger />,
            ]}
          />
          <ConfirmDialog isLoading={deleteMutation.isPending} onConfirm={handleDelete} {...dialog} />
        </Table.Col>
      </Table.Row>
    </Fragment>
  );
}

export function StandardRecordListTable({ records, isLoading }: Props) {
  const { data, isLoading: isDisciplineLoading } = useStandardRecordDisciplineListQuery();

  return (
    <Table bg="even">
      <Table.Head>
        <Table.Row>
          <Table.Col as="th">Distrikt</Table.Col>
          <Table.Col as="th">Tävlingstyp</Table.Col>
          <Table.Col as="th">Dam/Herr</Table.Col>
          <Table.Col as="th">Ålderskategori</Table.Col>
          <Table.Col as="th">Viktklass</Table.Col>
          <Table.Col as="th">Disciplin</Table.Col>
          <Table.Col as="th">Resultat</Table.Col>
          <Table.Col />
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {isLoading || isDisciplineLoading ? (
          <Table.Loading cols={8} />
        ) : (
          records.map(record => <Row key={`standard-record-${record.id}`} record={record} disciplines={data ?? []} />)
        )}
      </Table.Body>
    </Table>
  );
}
